import { default as deserialize } from 'app/utils/deserialize'
import { fetch, remove, update } from 'app/utils/data'
import { getClientId } from 'app/utils/data/socket'
import get from 'lodash/get'
import find from 'lodash/find'
import size from 'lodash/size'
import FormErrors from 'app/utils/form_errors'
import sortBy from 'lodash/sortBy'
import moment from 'moment'
import axios from 'axios'
import md5 from 'blueimp-md5'

const IMAGE_UPLOAD_INFINITY = -1

// Stable JSON-API error `code` the server sends when an upload is rejected for
// exceeding the plan allowance. Keep in sync with the controller's
// `:upload_limit_exceeded` clause (image_upload_controller.ex).
export const IMAGE_UPLOAD_LIMIT_ERROR_CODE = 'image_upload_limit_exceeded'

// Mirrors `Fontawesome.Clouds.ImageUpload.max_file_size_bytes/0` on the
// server (lib/fontawesome/clouds/image_uploads/image_upload.ex). Keep in sync.
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

export const IMAGE_UPLOAD_STATUS = Object.freeze({
  idle: 'idle',
  uploading: 'uploading',
  processing: 'processing',
  complete: 'complete',
  failed: 'failed'
})

function resolveErrors(error) {
  return get(error, 'response.data.errors', null) || [{ detail: 'Something went wrong. Please try again.' }]
}

export default {
  namespaced: true,

  state: {
    list: [],
    pending: {}
  },

  getters: {
    currentImageUploads: (state) => state.list,
    totalUploaded: (_state, _getters, _rootState, rootGetters) => {
      const allKits = rootGetters['kits/currentKits']

      return allKits.reduce((acc, kit) => {
        acc = acc + get(kit, ['imageUploads', 'length'], 0)

        return acc
      }, 0)
    },
    totalUploadedWithPending: (state, getters, _rootState, rootGetters) => {
      return getters['totalUploaded'] + size(state.pending)
    },
    uploadLimit: (_state, _getters, _rootState, rootGetters) => {
      return get(rootGetters['permits/find']('accessToImageUploads'), ['permit', 'imageUploadLimit'], 0)
    },
    infiniteLimit: (_state, getters) => {
      return getters['uploadLimit'] === IMAGE_UPLOAD_INFINITY
    },
    uploadLimitReached: (_state, getters) => {
      return !getters['infiniteLimit'] && getters['totalUploadedWithPending'] >= getters['uploadLimit']
    },
    uploadLimitExceeded: (_state, getters) => {
      return !getters['infiniteLimit'] && getters['totalUploadedWithPending'] > getters['uploadLimit']
    },
    sortedImageUploads: (state) => {
      const val = sortBy(state.list, (k) => moment.utc(k.updatedAt).unix())

      val.reverse()

      return val
    },
    getPending: (state) => (key) => state.pending[key],
    pendingImageUploads: (state) => state.pending
  },

  mutations: {
    imageUploadsFetched(state, { imageUploads }) {
      state.list = imageUploads
    },

    imageUploadFetched(state, { imageUpload }) {
      if (!find(state.list, { id: imageUpload.id })) {
        state.list.push(imageUpload)
      }
    },

    imageUploadDeleted(state, { id }) {
      state.list = state.list.filter((imageUploads) => {
        return imageUploads.id !== id
      })
    },

    resetState(state) {
      state.list.length = 0
      state.pending = {}
    },

    setPending(state, pendingImages = {}) {
      for (const key in pendingImages) {
        state.pending[key] = pendingImages[key]
      }
    },

    deletePending(state, keyOrKeys = []) {
      if ('string' === typeof keyOrKeys) {
        delete state.pending[keyOrKeys]
      } else if (Array.isArray(keyOrKeys)) {
        for (const key of keyOrKeys) {
          delete state.pending[key]
        }
      }
    },

    /**
     * Shallow-merge fields onto a single pending row (Vue 2 reactivity).
     * Used when `ImageUploadView` syncs child edits or upload pipeline status.
     */
    patchPending(state, { key, patch }) {
      if (!state.pending[key]) return
      state.pending[key] = { ...state.pending[key], ...patch }
    }
  },

  actions: {
    /**
     * Stage raster files for the image upload review screen (picker + drag).
     * Mirrors dropzone intake: same keys (`md5(name-size-mtime)`), 10MB cap,
     * and full row shape for `ImageUploadView` / `Upload.vue`.
     */
    stageRasterFiles({ commit }, { files }) {
      const pending = {}
      const list = Array.isArray(files) ? files : []

      for (const file of list) {
        if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') continue

        const key = md5(`${file.name}-${file.size}-${file.lastModified}`)

        pending[key] = {
          file,
          fileName: file.name,
          name: file.name,
          thumbnail: URL.createObjectURL(file),
          errors: [],
          status: IMAGE_UPLOAD_STATUS.idle,
          previewError: false
        }
      }

      if (Object.keys(pending).length) {
        commit('setPending', pending)
      }
    },

    async fetchImageUploads({ commit }, kitToken) {
      try {
        const response = await fetch({ url: `/api/clouds/kits/${kitToken}/image-uploads` })
        const uploads = await deserialize(response.data)
        commit('imageUploadsFetched', { imageUploads: uploads })
      } catch (err) {
        throw new FormErrors(resolveErrors(err))
      }
    },

    async createImageUpload({ commit }, { token, file, name }) {
      try {
        const form = new FormData()
        form.append('image', file)
        form.append('data[type]', 'image-uploads')
        form.append('data[attributes][name]', name)
        // Echoed back on the completion push so the originating tab matches it.
        form.append('data[attributes][clientId]', getClientId())

        const resp = await axios.post(`/api/clouds/kits/${token}/image-uploads`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
        const imageUpload = await deserialize(resp.data)

        commit('imageUploadFetched', { imageUpload })
        return imageUpload
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async updateImageUpload({ commit }, { kitToken, id, changes }) {
      try {
        const response = await update({
          url: `/api/clouds/kits/${kitToken}/image-uploads/${id}`,
          patch: true,
          type: 'image-upload',
          changes
        })

        const imageUpload = await deserialize(response.data)

        commit('imageUploadFetched', { imageUpload })

        return imageUpload
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async deleteImageUpload({ commit, dispatch }, { kitToken, id }) {
      try {
        await remove({
          url: `/api/clouds/kits/${kitToken}/image-uploads/${id}`,
          type: 'image-upload'
        })

        dispatch('kits/fetchKit', kitToken, { root: true })
        commit('imageUploadDeleted', { id })
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    }
  }
}
